<template>
  <div>
    <h1 class="text-center my-2">Assemblies</h1>
    <location-code-guide level="assembly" />
    <add-or-update-assembly-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData" @resetUpdateData="resetUpdateData"
    />

    <list-table :records="records" :columns="tableColumns" :total-records="totalRecords" :search-filter.sync="search"
      :current-page-number.sync="currentPage" :show-search="true" :show-filter-by-default="true"
      :show-export-button="can('assemblies:CanExport')" @export="onExport"
>
      <template #filters>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <v-select
              :value="parentTypeFilter"
              placeholder="Belongs To"
              :clearable="true"
              :options="parentTypeOptions"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @input="onParentTypeFilterChange"
            />
          </b-col>

          <b-col v-if="parentTypeFilter !== 'country'" cols="12" md="3" class="mb-1">
            <v-select
              :value="regionFilter"
              placeholder="Select Region"
              :loading="regionsLoading"
              :clearable="true"
              :options="regionOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @input="onRegionFilterChange"
            />
          </b-col>

          <b-col v-if="parentTypeFilter !== 'region'" cols="12" md="3" class="mb-1">
            <v-select
              :value="countryFilter"
              placeholder="Select Country"
              :loading="countriesLoading"
              :clearable="true"
              :options="countryOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @input="onCountryFilterChange"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              :value="areaFilter"
              placeholder="Select Area"
              :loading="areasLoading"
              :clearable="true"
              :options="areaOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @search="onSearchArea"
              @input="onAreaFilterChange"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              v-model="districtFilter"
              placeholder="Select District"
              :loading="districtsLoading"
              :clearable="true"
              :options="districtOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @search="onSearchDistrict"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              v-model="assemblyTypeFilter"
              placeholder="Assembly Type"
              :clearable="true"
              :options="assemblyTypeOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              v-model="missionZoneFilter"
              placeholder="Is Mission Zone?"
              :clearable="true"
              :options="yesNoOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              v-model="statusFilter"
              placeholder="Status"
              :clearable="false"
              :options="locationStatusOptions"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <v-select
              v-model="sortFilter"
              placeholder="Sort by"
              :clearable="false"
              :options="locationSortOptions"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>

          <b-col v-if="hasActiveFilters" cols="12" md="3" class="mb-1 d-flex align-items-center">
            <b-button variant="outline-secondary" size="sm" @click="clearFilters">
              <feather-icon icon="XIcon" class="mr-50" />
              <span class="align-middle">Clear filters</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <template v-if="can('assemblies:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add assembly</span>
        </b-button>
      </template>

      <template #cell(parent_type)="data">
        <b-badge
          pill
          :variant="`light-${getValueFromSource(data.item, 'district.area.region_id') ? 'primary' : 'info'}`"
          class="text-capitalize"
        >
          {{ getValueFromSource(data.item, 'district.area.region_id') ? 'Region' : 'Country (direct)' }}
        </b-badge>
      </template>

      <template #cell(mission_zone)="data">
        <b-badge pill :variant="`light-${resolveStatusVariant(data.item.mission_zone ? 'yes' : 'no')}`"
          class="text-capitalize"
>
          {{ data.item.mission_zone ? "YES" : "NO" }}
        </b-badge>
      </template>

      <template #cell(is_active)="data">
        <b-badge pill :variant="isLocationActive(data.item) ? 'light-success' : 'light-secondary'">
          {{ isLocationActive(data.item) ? 'Active' : 'Inactive' }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
          </template>

          <b-dropdown-item v-if="can('assemblies:CanUpdate')" @click="toggleMissionZone(data.item)">
            <feather-icon icon="MapPinIcon" />
            <span class="align-middle ml-50">
              {{ !data.item.mission_zone ? 'Mark as Mission Zone' : 'Unmark as Mission Zone' }}
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('assemblies:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">
              Edit
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('assemblies:CanUpdate')" @click="onToggleStatus(data.item)">
            <feather-icon :icon="isLocationActive(data.item) ? 'EyeOffIcon' : 'EyeIcon'" />
            <span class="align-middle ml-50">
              {{ isLocationActive(data.item) ? 'Deactivate' : 'Activate' }}
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('assemblies:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">
              Delete
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import FileDownload from "js-file-download";
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateAssemblyForm from '@/@core/components/shared/geo/AddOrUpdateAssemblyForm.vue';
import LocationCodeGuide from '@/@core/components/shared/geo/LocationCodeGuide.vue';
import LocationSortOptions from '@core/mixins/LocationSortOptions';
import LocationStatusOptions from '@core/mixins/LocationStatusOptions';

import { debounce, get } from "lodash";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    LocationCodeGuide,
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateAssemblyForm,
  },
  directives: {
    Ripple,
  },
  mixins: [LocationSortOptions, LocationStatusOptions],
  data() {
    return {
      loading: false,
      records: [],

      regionOptions: [],
      countryOptions: [],
      areaOptions: [],
      districtOptions: [],

      regionsLoading: false,
      countriesLoading: false,
      areasLoading: false,
      districtsLoading: false,

      parentTypeFilter: null,
      regionFilter: null,
      countryFilter: null,
      areaFilter: null,
      districtFilter: null,
      assemblyTypeFilter: null,
      missionZoneFilter: null,

      parentTypeOptions: [
        { label: 'Region', value: 'region' },
        { label: 'Country (direct)', value: 'country' },
      ],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'title', label: "Assembly", sortable: false },
        { key: 'code', label: "Code", sortable: false, formatter: val => val || '-' },
        { key: 'assembly_type', label: 'Type', sortable: false },
        {
          key: 'parent_type',
          label: 'Belongs To',
          sortable: false,
          formatter: (_, __, item) => (get(item, 'district.area.region_id') ? 'Region' : 'Country (direct)'),
        },
        {
          key: 'district.title',
          label: "District",
          sortable: false,
          formatter: (value, _, item) => {
            if (!value) return '-'
            const code = get(item, 'district.code')
            return code ? `${value} (${code})` : value
          },
        },
        { key: 'district.area.title', label: "Area", sortable: false, formatter: val => val || '-' },
        {
          key: 'district.area.region.title',
          label: 'Region / RMC',
          sortable: false,
          formatter: (value, _, item) => {
            if (get(item, 'district.area.region_id')) {
              return value || '-'
            }
            return get(item, 'district.area.country.rmc.title') || 'No RMC Assigned'
          },
        },
        { key: 'district.area.country.country', label: 'Country', sortable: false, formatter: val => val || '-' },
        { key: 'mission_zone', label: 'Mission Zone', sortable: false },
        { key: 'is_active', label: 'Status', sortable: false },
        // { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
    activeFilters() {
      return [
        this.parentTypeFilter,
        this.regionFilter,
        this.countryFilter,
        this.areaFilter,
        this.districtFilter,
        this.assemblyTypeFilter,
        this.missionZoneFilter,
      ].filter(Boolean)
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0 || !this.isDefaultLocationSort || !this.isDefaultLocationStatus
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
    parentTypeFilter: watchHandlerRequiresPageReset,
    regionFilter: watchHandlerRequiresPageReset,
    countryFilter: watchHandlerRequiresPageReset,
    areaFilter: watchHandlerRequiresPageReset,
    districtFilter: watchHandlerRequiresPageReset,
    assemblyTypeFilter: watchHandlerRequiresPageReset,
    missionZoneFilter: watchHandlerRequiresPageReset,
    sortFilter: watchHandlerRequiresPageReset,
    statusFilter: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage,
      parent_type,
      region_id,
      country_id,
      area_id,
      district_id,
      assembly_type,
      mission_zone,
    } = this.$route.query;

    // Restoring straight onto the filter state (rather than through the change
    // handlers) is deliberate: the handlers cascade-clear their children, which
    // would wipe the rest of the filters coming out of the url.
    if (parent_type) {
      this.parentTypeFilter = parent_type
    }

    if (region_id) {
      this.regionFilter = region_id
    }

    if (country_id) {
      this.countryFilter = country_id
    }

    if (area_id) {
      this.areaFilter = area_id
      this.addSingleAreaOption(area_id)
    }

    if (district_id) {
      this.districtFilter = district_id
      this.addSingleDistrictOption(district_id)
    }

    if (assembly_type) {
      this.assemblyTypeFilter = assembly_type
    }

    if (mission_zone) {
      this.missionZoneFilter = mission_zone
    }

    this.restoreLocationSortFromQuery(this.$route.query);
    this.restoreLocationStatusFromQuery(this.$route.query);

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;

    this.fetchRegions();
    this.fetchCountries();
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
          ...this.buildFilterQuery(),
        }

        await this.$router.push({ query }).catch(() => { });

        const request = await this.useJwt().sharedService.fetchGeoAssemblies(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.deleteGeoAssemblies(id)

        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.toast({
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'error',
            text: error_message
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async toggleMissionZone(assembly) {
      try {
        this.loading = true;

        const mark = assembly.mission_zone ? "mark" : "unmark";

        const result = await this.$swal({
          title: 'Confirm Mission Zone Update?',
          text: `Do you want to ${mark} this assembly as a mission zone?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.updateGeoAssemblies(assembly.id, { mission_zone: !assembly.mission_zone });
        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'District updated successfully.'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onToggleStatus(assembly) {
      return this.toggleLocationStatus(assembly, {
        serviceMethod: "updateGeoAssemblyStatus",
        label: "Assembly",
        // Assemblies are the leaf level -- nothing hangs off them.
        cascades: false,
      });
    },
    buildFilterQuery() {
      const query = {};
      if (this.parentTypeFilter) query.parent_type = this.parentTypeFilter;
      if (this.regionFilter) query.region_id = this.regionFilter;
      if (this.countryFilter) query.country_id = this.countryFilter;
      if (this.areaFilter) query.area_id = this.areaFilter;
      if (this.districtFilter) query.district_id = this.districtFilter;
      if (this.assemblyTypeFilter) query.assembly_type = this.assemblyTypeFilter;
      if (this.missionZoneFilter) query.mission_zone = this.missionZoneFilter;
      Object.assign(query, this.locationSortQuery, this.locationStatusQuery);
      return query;
    },
    async onExport() {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current search and filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.search,
          ...this.buildFilterQuery(),
        }

        const response = await this.useJwt().sharedService.exportGeoAssemblies(query);
        FileDownload(response.data, "assemblies-export.xlsx");
      } catch (error) {
        const error_message = get(error, "response.statusText") || get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update) {
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null
      }
    },
    onParentTypeFilterChange(value) {
      this.parentTypeFilter = value;
      this.regionFilter = null;
      this.countryFilter = null;
      this.clearAreaAndDistrictFilters();
    },
    onRegionFilterChange(value) {
      this.regionFilter = value;

      if (value) {
        this.countryFilter = null;
      }

      this.clearAreaAndDistrictFilters();
      this.loadAreaOptions();
    },
    onCountryFilterChange(value) {
      this.countryFilter = value;

      if (value) {
        this.regionFilter = null;
      }

      this.clearAreaAndDistrictFilters();
      this.loadAreaOptions();
    },
    onAreaFilterChange(value) {
      this.areaFilter = value;
      this.districtFilter = null;
      this.districtOptions = [];

      if (value) {
        this.loadDistrictOptions();
      }
    },
    clearAreaAndDistrictFilters() {
      this.areaFilter = null;
      this.districtFilter = null;
      this.areaOptions = [];
      this.districtOptions = [];
    },
    clearFilters() {
      this.parentTypeFilter = null;
      this.regionFilter = null;
      this.countryFilter = null;
      this.assemblyTypeFilter = null;
      this.missionZoneFilter = null;
      this.resetLocationSort();
      this.resetLocationStatus();
      this.clearAreaAndDistrictFilters();
    },
    async fetchRegions() {
      try {
        this.regionsLoading = true;

        const request = await this.useJwt().sharedService.fetchGeoRegions();
        const { data } = request.data;

        this.regionOptions = (data || []).map((item) => ({
          label: this.geoOptionLabel(item.title, item.code),
          value: item._id
        }))
      } catch (error) {
        this.onFilterError(error)
      } finally {
        this.regionsLoading = false;
      }
    },
    async fetchCountries() {
      try {
        this.countriesLoading = true;

        const request = await this.useJwt().sharedService.fetchCountries();
        const { data } = request.data;

        this.countryOptions = (data || []).map((item) => ({
          label: item.country,
          value: item._id
        }))
      } catch (error) {
        this.onFilterError(error)
      } finally {
        this.countriesLoading = false;
      }
    },
    // Areas are scoped to whatever ancestor filter is set, so the dropdown only
    // ever offers areas that can actually hold a matching assembly.
    areaQueryScope() {
      const query = {}

      if (this.regionFilter) {
        query.region_id = this.regionFilter
      }

      if (this.countryFilter) {
        query.country_id = this.countryFilter
      }

      if (this.parentTypeFilter && !this.regionFilter && !this.countryFilter) {
        query.parent_type = this.parentTypeFilter
      }

      return query
    },
    async loadAreaOptions(search = "") {
      try {
        this.areasLoading = true;

        const scope = this.areaQueryScope();
        const query = {
          ...scope,
          search,
          limit: Object.keys(scope).length ? 500 : 100,
        }

        const request = await this.useJwt().sharedService.fetchGeoAreas(query);
        const { data } = request.data;

        this.areaOptions = (data || []).map((item) => ({
          label: this.geoOptionLabel(item.title, item.code),
          value: item._id
        }))
      } catch (error) {
        this.onFilterError(error)
      } finally {
        this.areasLoading = false;
      }
    },
    async loadDistrictOptions(search = "") {
      try {
        this.districtsLoading = true;

        const query = { search, limit: 100 }

        if (this.areaFilter) {
          query.area_id = this.areaFilter
          query.limit = 500
        }

        const request = await this.useJwt().sharedService.fetchGeoDistricts(query);
        const { data } = request.data;

        this.districtOptions = (data || []).map((item) => ({
          label: this.districtOptionLabel(item),
          value: item._id
        }))
      } catch (error) {
        this.onFilterError(error)
      } finally {
        this.districtsLoading = false;
      }
    },
    async onSearchArea(search, loading) {
      if (!search) {
        return;
      }

      loading(true);
      await this.loadAreaOptions(search);
      loading(false);
    },
    async onSearchDistrict(search, loading) {
      if (!search) {
        return;
      }

      loading(true);
      await this.loadDistrictOptions(search);
      loading(false);
    },
    async addSingleAreaOption(id) {
      try {
        const request = await this.useJwt().sharedService.getGeoArea(id);
        const { data } = request.data;

        this.areaOptions = [{
          label: this.geoOptionLabel(data.title, data.code),
          value: data._id
        }]
      } catch (error) {
        this.onFilterError(error)
      }
    },
    async addSingleDistrictOption(id) {
      try {
        const request = await this.useJwt().sharedService.getGeoDistrict(id);
        const { data } = request.data;

        this.districtOptions = [{
          label: this.districtOptionLabel(data),
          value: data._id
        }]
      } catch (error) {
        this.onFilterError(error)
      }
    },
    geoOptionLabel(title, code) {
      return code ? `${title} (${code})` : title
    },
    // Districts belong to an area, so surface the area's name/code alongside
    // the district in the filter dropdown for disambiguation.
    districtOptionLabel(district) {
      const districtLabel = this.geoOptionLabel(district.title, district.code)
      const areaTitle = get(district, "area.title")

      if (!areaTitle) {
        return districtLabel
      }

      const areaLabel = this.geoOptionLabel(areaTitle, get(district, "area.code"))

      return `${districtLabel} - ${areaLabel}`
    },
    onFilterError(error) {
      const error_message = get(error, "response.data.message") || error.message

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error!',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: error_message
        },
      });
    },
  }
}
</script>
